<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ArrowLeft } from '@lucide/vue'
import { Button, Select, Spinner, Textarea, useToast } from '@drama/ui'

import { adoptNovelComparison, cancelNovelComparison, createNovelComparison, getNovelComparison, runNovelComparisonTurn } from '../../api/novel/comparisons.js'
import { useAuditText } from '../../components/novel/audit/audit-text.js'
import ComparisonSide from '../../components/novel/comparison/ComparisonSide.vue'
import { useNovelPageText } from '../../components/novel/novel-page-text.js'
import '../../components/novel/audit/audit.css'

defineOptions({ name: 'NovelModelComparison' })

const MODES = ['mixed', 'speech', 'action', 'continue']
// 与游玩回合同一上限：对比回合走同一个回合运行器，按 1000 字校验。
const MAX_INPUT = 1000
// 生成中轮询对比详情，取两边对局视图里的流式草稿。
const POLL_MS = 1500

const props = defineProps({ comparisonId: { type: String, required: true } })

const { t } = useI18n()
const tc = (key, params) => t(`visualGameAdmin.novelComparison.${key}`, params ?? {})
const { enumLabel } = useAuditText()
const { apiError, dateTime } = useNovelPageText()
const router = useRouter()
const toast = useToast()

const comparison = ref(null)
const loading = ref(false)
const error = ref(null)
// 进行中的操作：turn 生成、adopt 采纳、create 重建对比；空串表示空闲。
const busy = ref('')
// 输入与模型是否在准备下一回合：还没生成过，或点了「换模型再比一回合」。
const editing = ref(true)
const mode = ref('mixed')
const text = ref('')
const modelIds = ref(['', ''])
const cancelling = ref('')
let pollTimer = null
let disposed = false
let page_version = 0

const sides = computed(() => comparison.value?.sides ?? [])
const generating = computed(() => busy.value === 'turn' || sides.value.some((side) => side.status === 'generating'))
// 生成中不许改模型、改输入（设计 §3）。
const locked = computed(() => !editing.value || generating.value || busy.value !== '')
const modeOptions = computed(() => MODES.map((value) => ({ value, label: enumLabel('turns.modes', value) })))
const configured = computed(() => modelIds.value.every((id) => comparison.value?.models.some((model) => model.id === id && model.configured)))
const canSend = computed(() => !locked.value && configured.value && text.value.length <= MAX_INPUT && (mode.value === 'continue' || text.value.trim() !== ''))
const playerLabel = computed(() => (comparison.value?.source ? comparison.value.source.user_email ?? tc('playerUnknown') : tc('sourceMissing')))
const backTarget = computed(() => (comparison.value?.source ? { name: 'novel-run-audit', params: { runId: comparison.value.source.run_id } } : { name: 'novel-runs' }))

onMounted(load)
onBeforeUnmount(() => {
  disposed = true
  page_version += 1
  window.clearTimeout(pollTimer)
})
watch(() => props.comparisonId, () => {
  page_version += 1
  window.clearTimeout(pollTimer)
  comparison.value = null
  modelIds.value = ['', '']
  busy.value = ''
  cancelling.value = ''
  load()
})
// 导演对比两边同一个写作模型：B 边跟着 A 边。
watch(() => modelIds.value[0], (value) => {
  if (comparison.value?.director_comparison) modelIds.value[1] = value
})

function install(next) {
  comparison.value = next
  // 模型选择以服务端记下的为准；正在准备下一回合时保留页面上改过的选择。
  if (!editing.value || modelIds.value.includes('')) modelIds.value = next.sides.map((side) => side.model_id ?? '')
  const last = next.sides.find((side) => side.input)?.input
  if (!editing.value && last) {
    mode.value = last.mode
    text.value = last.text
  }
  schedulePoll()
}

async function load() {
  const version = page_version
  loading.value = true
  error.value = null
  try {
    const next = await getNovelComparison(props.comparisonId)
    if (disposed || version !== page_version) return
    editing.value = next.sides.every((side) => side.status === 'ready')
    install(next)
  } catch (requestError) {
    if (!disposed && version === page_version) error.value = apiError(requestError)
  } finally {
    if (version === page_version) loading.value = false
  }
}

async function refresh() {
  const version = page_version
  try {
    const next = await getNovelComparison(props.comparisonId)
    if (!disposed && version === page_version) install(next)
  } catch (requestError) {
    if (!disposed && version === page_version) error.value = apiError(requestError)
  }
}

function schedulePoll() {
  window.clearTimeout(pollTimer)
  if (disposed || !generating.value) return
  pollTimer = window.setTimeout(async () => {
    await refresh()
    schedulePoll()
  }, POLL_MS)
}

async function runRound(body) {
  const version = page_version
  busy.value = 'turn'
  error.value = null
  editing.value = false
  schedulePoll()
  try {
    const next = await runNovelComparisonTurn(props.comparisonId, body)
    if (!disposed && version === page_version) install(next)
  } catch (requestError) {
    if (disposed || version !== page_version) return
    // 回合请求可能在生成途中断开（网关超时等）：先读一次真实状态，还在生成就接着轮询；确实没开始才显示原因并放开输入。
    await refresh()
    if (!sides.value.some((side) => side.status === 'generating')) {
      error.value = apiError(requestError)
      editing.value = sides.value.every(side => side.status === 'ready')
    }
  } finally {
    if (version === page_version) {
      busy.value = ''
      schedulePoll()
    }
  }
}

function send() {
  if (!canSend.value) return
  runRound({ mode: mode.value, text: mode.value === 'continue' ? '' : text.value, performer_ids: [...modelIds.value] })
}

function retry(side) {
  runRound({ mode: side.input.mode, text: side.input.text, side: side.side })
}

async function cancel(side) {
  if (cancelling.value) return
  cancelling.value = side.side
  try { install(await cancelNovelComparison(props.comparisonId, side.side)) }
  catch (requestError) { error.value = apiError(requestError) }
  finally { cancelling.value = '' }
}

async function adopt(side) {
  busy.value = 'adopt'
  error.value = null
  try {
    install(await adoptNovelComparison(props.comparisonId, side.side))
    toast.success(tc('adoptedToast', { count: side.added_turns }))
  } catch (requestError) {
    error.value = apiError(requestError)
  } finally {
    busy.value = ''
  }
}

/** 选这一边为共同起点：复制同一份胜者快照，下一轮两边仍使用相同上下文。 */
async function chooseBaseline(side) {
  if (generating.value || busy.value !== '') return
  busy.value = 'create'
  error.value = null
  try {
    const body = { performer_ids: [...modelIds.value], ...(comparison.value.director_comparison ? { director_modes: [false, true] } : {}) }
    const next = await createNovelComparison(side.run_id, body)
    editing.value = true
    text.value = ''
    await router.replace({ name: 'novel-comparison', params: { comparisonId: next.comparison_id } })
  } catch (requestError) { error.value = apiError(requestError) }
  finally { busy.value = '' }
}

/** 回到源对局当前进度重建对比（重新开始，或切换"只比导演开/关"）。 */
async function recreate(directorComparison) {
  const source = comparison.value?.source
  if (!source || generating.value || busy.value !== '') return
  busy.value = 'create'
  error.value = null
  try {
    const [first, second] = modelIds.value
    const body = directorComparison ? { performer_ids: [first, first], director_modes: [false, true] } : { performer_ids: [first, second] }
    const next = await createNovelComparison(source.run_id, body)
    editing.value = true
    text.value = ''
    await router.replace({ name: 'novel-comparison', params: { comparisonId: next.comparison_id } })
  } catch (requestError) {
    error.value = apiError(requestError)
  } finally {
    busy.value = ''
  }
}

function speakerName(speakerId) {
  if (speakerId === 'narrator') return tc('narrator')
  return sides.value[0]?.view.characters.find((person) => person.id === speakerId)?.name ?? '—'
}
</script>

<template>
  <div class="vga-page au cmp" data-insp-path="apps/visual-game-admin/src/pages/novel/Comparison.vue" data-testid="model-comparison">
    <div v-if="loading && !comparison" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <template v-else-if="comparison">
      <header class="cmp-head">
        <div>
          <h1 class="cmp-head__title">{{ tc('title') }}</h1>
          <p class="cmp-head__sub">
            <span>{{ comparison.pack_title }}</span>
            <span>{{ playerLabel }}</span>
            <span>{{ tc('fromTurn', { count: comparison.baseline.through_seq }) }}</span>
          </p>
          <p class="cmp-note">{{ tc('expires', { time: dateTime(comparison.expires_at) }) }}</p>
        </div>
        <RouterLink class="cmp-head__back" :to="backTarget"><ArrowLeft :size="14" aria-hidden="true" />{{ tc('back') }}</RouterLink>
      </header>
      <p v-if="error" class="vga-error" role="alert" :title="error.title">{{ error.text }}</p>
      <details class="cmp-card">
        <summary class="cmp-card__summary">{{ tc('history') }}</summary>
        <div class="au-lines cmp-history">
          <p
            v-for="(segment, index) in comparison.baseline.history_tail"
            :key="segment.segment_id || index"
            class="au-line"
            :class="[`cmp-line--${segment.channel}`, { 'is-player': segment.speaker_id === sides[0].view.player_id }]"
          >
            <span class="au-line__who">{{ speakerName(segment.speaker_id) }}<small>{{ enumLabel('story.channels', segment.channel) }}</small></span>
            <span class="au-line__text">{{ segment.text }}</span>
          </p>
          <p v-if="!comparison.baseline.history_tail.length" class="cmp-note">{{ tc('empty') }}</p>
        </div>
      </details>
      <section class="cmp-card cmp-input">
        <div class="cmp-input__row">
          <label class="cmp-field">
            <span class="cmp-field__label">{{ tc('inputMode') }}</span>
            <Select v-model="mode" :options="modeOptions" :disabled="locked" :aria-label="tc('inputMode')" block />
          </label>
          <label class="cmp-field">
            <span class="cmp-field__label">{{ tc('input') }}</span>
            <Textarea v-model="text" :rows="3" :maxlength="MAX_INPUT" :disabled="locked || mode === 'continue'" :invalid="text.length > MAX_INPUT" data-testid="comparison-input" />
            <small class="cmp-note">{{ tc('inputHint', { max: MAX_INPUT }) }}</small>
          </label>
        </div>
        <div class="cmp-input__actions">
          <Button :loading="busy === 'turn'" :disabled="!canSend" data-testid="comparison-send" @click="send">{{ tc('send') }}</Button>
          <label class="cmp-toggle">
            <input type="checkbox" :checked="comparison.director_comparison" :disabled="generating || busy !== '' || !comparison.source" @change="recreate($event.target.checked)" />
            {{ tc('directorToggle') }}
          </label>
        </div>
      </section>
      <div class="cmp-grid">
        <ComparisonSide
          v-for="(side, index) in sides"
          :key="side.run_id"
          :side="side"
          :models="comparison.models"
          :model-id="modelIds[index]"
          :model-locked="locked || (comparison.director_comparison && index === 1)"
          :director-comparison="comparison.director_comparison"
          :adoptable="comparison.source?.adoptable ?? false"
          :adopted-side="comparison.adopted_side"
          :busy="generating || busy !== ''"
          :cancelling="cancelling === side.side"
          @update:model-id="modelIds[index] = $event"
          @retry="retry"
          @adopt="adopt"
          @cancel="cancel"
          @advance="chooseBaseline"
        />
      </div>
      <div class="cmp-foot">
        <p class="cmp-note">{{ t('novelPlay.comparison.commonBaselineHelp') }}</p>
        <Button variant="secondary" :loading="busy === 'create'" :disabled="generating || busy !== '' || !comparison.source" @click="recreate(comparison.director_comparison)">{{ tc('restart') }}</Button>
      </div>
    </template>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error" :title="error.title">{{ error.text }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
  </div>
</template>

<style scoped>
.cmp { display: grid; gap: var(--drama-space-lg); max-width: 1280px; margin-inline: auto; }
.cmp-head { display: flex; justify-content: space-between; align-items: flex-start; gap: var(--drama-space-lg); }
.cmp-head__title { margin: 0; font-size: 18px; font-weight: 600; }
.cmp-head__sub { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); margin: var(--drama-space-xs) 0 0; font-size: 13px; }
.cmp-head__sub span + span::before { content: '·'; margin-inline-end: var(--drama-space-sm); color: var(--drama-color-text-muted); }
.cmp-head__back { display: inline-flex; align-items: center; gap: var(--drama-space-xs); color: var(--drama-color-text-muted); font-size: 13px; white-space: nowrap; }
.cmp-note { margin: var(--drama-space-xs) 0 0; color: var(--drama-color-text-muted); font-size: 12px; }
.cmp-card { padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); }
.cmp-card__summary { cursor: pointer; font-weight: 600; }
.cmp-history { margin-top: var(--drama-space-sm); }
.cmp-input { display: grid; gap: var(--drama-space-md); }
.cmp-input__row { display: grid; grid-template-columns: 220px minmax(0, 1fr); gap: var(--drama-space-md); align-items: start; }
.cmp-field { display: grid; gap: var(--drama-space-xs); }
.cmp-field__label { color: var(--drama-color-text-muted); font-size: 12px; }
.cmp-input__actions { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-lg); }
.cmp-toggle { display: inline-flex; align-items: center; gap: var(--drama-space-xs); font-size: 13px; }
.cmp-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--drama-space-lg); align-items: start; }
.cmp-foot { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.cmp-line--narration .au-line__text { font-style: italic; }
.cmp-line--action .au-line__text { color: var(--drama-color-text-muted); }
@media (max-width: 960px) {
  .cmp-grid, .cmp-input__row { grid-template-columns: minmax(0, 1fr); }
}
</style>
